import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  alpha,
  IconButton,
  InputAdornment,
  useTheme,
  CircularProgress,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Store,
  Email,
  Lock,
  ArrowBack,
  Person,
  CheckCircle,
  CreditCard,
  Security,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { login } from '../../services/auth';
import { sessionManager } from '../../context/sessionManager';
import { useNavigate } from 'react-router-dom';

// Terms & Conditions Modal Component
const TermsAndConditionsModal = ({ open, onClose, isDark }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "20px",
        background: isDark
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
        backdropFilter: "blur(30px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        boxShadow: isDark
          ? "0 20px 60px rgba(0, 0, 0, 0.4)"
          : "0 20px 60px rgba(0, 0, 0, 0.1)",
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 3,
        background: isDark
          ? "linear-gradient(90deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)"
          : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
        borderBottom: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          background: isDark
            ? "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)"
            : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.02em",
        }}
      >
        Terms & Conditions
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: isDark ? "#94a3b8" : "#64748b",
          mt: 1,
        }}
      >
        Please read these terms carefully before using our billing and inventory
        software
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ p: 3 }}>
      <Box
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          pr: 2,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDark ? alpha("#1e293b", 0.5) : alpha("#f1f5f9", 0.5),
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "#475569" : "#cbd5e1",
            borderRadius: "10px",
          },
        }}
      >
        <Stack spacing={4}>
          {/* Section 1: Software Company Profile */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Store sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              1. Software Company Profile
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              About Us:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We are a technology-driven software company focused on building
              practical, reliable, and easy-to-use digital solutions for various
              businesses. Our goal is to simplify daily operations such as
              billing, inventory management, QR-based selling, customer records,
              and reports through secure and scalable software.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Vision:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              To digitally empower businesses of all sizes with affordable,
              efficient, and modern software solutions.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Mission:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>To create user-friendly accounting and inventory software</li>
              <li>
                To continuously improve features based on real business needs
              </li>
              <li>To provide reliable technical support and regular updates</li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Services Offered:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>Accounting Software</li>
              <li>Inventory & Stock Management</li>
              <li>Billing & Invoice Generation</li>
              <li>QR Code & Scanner Integration</li>
              <li>Cloud-based Backend Solutions</li>
              <li>Custom Feature Development</li>
            </Box>
          </Box>

          {/* Section 2: Software Developer Details */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Person sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              2. Software Developer Details
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Developer Role:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              The software is designed, developed, and maintained by the
              software developer/company with a strong understanding of both
              technology and business workflows.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Developer Responsibilities:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>
                Design and develop the software according to agreed requirements
              </li>
              <li>Fix reported bugs and technical issues</li>
              <li>Improve performance and security</li>
              <li>Add new features when mutually agreed</li>
              <li>Ensure data handling best practices</li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Limitations:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                fontStyle: "italic",
                mb: 2,
              }}
            >
              The developer is not responsible for:
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <li>Incorrect data entry by users</li>
                <li>
                  Business losses due to misuse or misunderstanding of the
                  software
                </li>
                <li>Internet, device, or third-party service failures.</li>
              </Box>
            </Typography>
          </Box>

          {/* Section 3: Software Mistakes & Improvement Policy */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              3. Software Mistakes & Improvement Policy
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We understand that no software is 100% error-free.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Mistake Handling Policy:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>
                If any software bug, calculation error, or technical issue is
                identified, it should be reported clearly by the client
              </li>
              <li>
                The developer will analyze the issue and confirm whether it is a
                software-related mistake
              </li>
              <li>
                Genuine software mistakes will be fixed without extra charges
                (within the support period)
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Improvement Clause:
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                background: isDark
                  ? alpha("#1e293b", 0.5)
                  : alpha("#f8fafc", 0.8),
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  fontStyle: "italic",
                }}
              >
                In case of any mistake or bug in the software, we assure
                continuous improvement and correction of the issue.
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Note:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>
                Feature changes or new requirements are not considered mistakes
                and may involve additional charges depends on developer.
              </li>
              <li>Timeline for fixes depends on the complexity of the issue</li>
            </Box>
          </Box>

          {/* Section 4: Software Payment Terms & Conditions */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CreditCard sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              4. Software Payment Terms & Conditions
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Subscription Model:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>
                The software is provided only on a yearly subscription basis
              </li>
              <li>No one-time / lifetime purchase option is available</li>
              <li>Subscription fees must be paid in advance for each year</li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Renewal Policy:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              The subscription must be renewed before the expiry date to
              continue using the software. Failure to renew on time may result
              in temporary suspension of software access.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Non-Refund Policy:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              All subscription payments are non-refundable. No refund will be
              provided for partial usage or early discontinuation.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Maintenance & Support:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              Software updates, bug fixes, and basic support are included during
              the active subscription period. Major feature additions or custom
              changes may be charged separately.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
              }}
            >
              Payment Delays:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              If yearly subscription payment is delayed, the company reserves
              the right to suspend services until payment is cleared.
            </Typography>
          </Box>

          {/* Section 5: Data & Responsibility Disclaimer */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Security sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              5. Data & Responsibility Disclaimer
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>
                The client is fully responsible for their business data entered
                into the software
              </li>
              <li>Regular data backup is recommended</li>
              <li>
                The software company/developer is not liable for data loss due
                to device failure, internet issues, or third-party service
                downtime
              </li>
            </Box>
          </Box>

          {/* Section 6: Acceptance of Terms */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              6. Acceptance of Terms
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              By using this software, the client agrees to all the above terms
              and conditions.
            </Typography>

            <Divider
              sx={{
                my: 2,
                borderColor: alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                  }}
                >
                  Last Updated: 1st Jan 2026
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  Authorized By: Darshan Jain
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Box>
    </DialogContent>
    <DialogActions
      sx={{
        p: 3,
        borderTop: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          borderRadius: "12px",
          fontWeight: 600,
          px: 4,
          py: 1,
          background: isDark
            ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
            : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isDark
              ? '0 12px 35px rgba(96, 165, 250, 0.4)'
              : '0 12px 35px rgba(37, 99, 235, 0.4)',
          },
        }}
      >
        I Understand
      </Button>
    </DialogActions>
  </Dialog>
);

// Privacy Policy Modal Component
const PrivacyPolicyModal = ({ open, onClose, isDark }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "20px",
        background: isDark
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
        backdropFilter: "blur(30px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        boxShadow: isDark
          ? "0 20px 60px rgba(0, 0, 0, 0.4)"
          : "0 20px 60px rgba(0, 0, 0, 0.1)",
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 3,
        background: isDark
          ? "linear-gradient(90deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)"
          : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
        borderBottom: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          background: isDark
            ? "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)"
            : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.02em",
        }}
      >
        Privacy Policy
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: isDark ? "#94a3b8" : "#64748b",
          mt: 1,
        }}
      >
        How we collect, use, and protect your personal information
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ p: 3 }}>
      <Box
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          pr: 2,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDark ? alpha("#1e293b", 0.5) : alpha("#f1f5f9", 0.5),
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "#475569" : "#cbd5e1",
            borderRadius: "10px",
          },
        }}
      >
        <Stack spacing={4}>
          {/* Section 1: Information We Collect */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Security sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              1. Information We Collect
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We collect information that you provide directly to us when using our software:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>Business name and contact information</li>
              <li>Email address and phone number</li>
              <li>Business transaction data (invoices, inventory, customers)</li>
              <li>Payment information for subscription management</li>
              <li>Usage data and software preferences</li>
            </Box>
          </Box>

          {/* Section 2: How We Use Your Information */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              2. How We Use Your Information
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>To provide and maintain our software services</li>
              <li>To process your subscription payments</li>
              <li>To send important notifications about your account</li>
              <li>To improve software features and user experience</li>
              <li>To provide customer support and technical assistance</li>
              <li>To comply with legal obligations</li>
            </Box>
          </Box>

          {/* Section 3: Data Security */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Lock sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              3. Data Security
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We implement appropriate technical and organizational security measures to protect your data:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure backup procedures</li>
              <li>Regular software updates and security patches</li>
            </Box>
          </Box>

          {/* Section 4: Data Sharing */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Person sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              4. Data Sharing
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We do not sell or rent your personal information to third parties. We may share data only in these circumstances:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>With payment processors for subscription management</li>
              <li>When required by law or legal process</li>
              <li>To protect our rights, property, or safety</li>
              <li>With your explicit consent</li>
            </Box>
          </Box>

          {/* Section 5: Your Rights */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              5. Your Rights
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              You have the right to:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to data processing</li>
              <li>Export your business data</li>
              <li>Withdraw consent at any time</li>
            </Box>
          </Box>

          {/* Section 6: Data Retention */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CreditCard sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              6. Data Retention
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We retain your data for as long as your account is active or as needed to provide services. After account termination, we may retain certain data for:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                mb: 2,
                "& li": {
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                  mb: 0.5,
                },
              }}
            >
              <li>Legal compliance requirements</li>
              <li>Dispute resolution</li>
              <li>Enforcement of agreements</li>
              <li>Business records (as required by law)</li>
            </Box>
          </Box>

          {/* Section 7: Contact Us */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDark ? "#60a5fa" : "#2563eb",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Email sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              7. Contact Us
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#cbd5e1" : "#475569",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              If you have questions about this Privacy Policy or how we handle your data, please contact us:
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                background: isDark
                  ? alpha("#1e293b", 0.5)
                  : alpha("#f8fafc", 0.8),
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? "#cbd5e1" : "#475569",
                  lineHeight: 1.8,
                }}
              >
                <strong>Email:</strong> jdarshan535@gmail.com<br />
                <strong>Phone No.:</strong> 9974706814<br />
                <strong>Last Updated:</strong> 1st Jan 2026
              </Typography>
            </Box>
          </Box>

          <Divider
            sx={{
              my: 2,
              borderColor: alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? "#94a3b8" : "#64748b",
                }}
              >
                Version: 2.0
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                }}
              >
                Authorized By: Darshan Jain
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>
    </DialogContent>
    <DialogActions
      sx={{
        p: 3,
        borderTop: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          borderRadius: "12px",
          fontWeight: 600,
          px: 4,
          py: 1,
          background: isDark
            ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
            : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isDark
              ? '0 12px 35px rgba(96, 165, 250, 0.4)'
              : '0 12px 35px rgba(37, 99, 235, 0.4)',
          },
        }}
      >
        I Understand
      </Button>
    </DialogActions>
  </Dialog>
);


// Step 3: Reset Password Component
const ResetPasswordForm = ({ isSubmitting, isDark, handleSubmit }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <Formik
      initialValues={{ newPassword: '', confirmPassword: '' }}
      validationSchema={Yup.object({
        newPassword: Yup.string()
          .required('New password is required')
          .min(8, 'Password must be at least 8 characters')
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            'Must contain uppercase, lowercase, number and special character'
          ),
        confirmPassword: Yup.string()
          .required('Please confirm your password')
          .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
      })}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched }) => (
        <Form>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              textAlign: 'center',
              mb: 3,
            }}>
              Create a new password for your account
            </Typography>

            {/* New Password Field */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{
                display: 'block',
                mb: 1,
                fontWeight: 600,
                color: isDark ? '#cbd5e1' : '#475569',
              }}>
                New Password
              </Typography>
              <Field name="newPassword">
                {({ field, form, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{
                            color: isDark ? '#60a5fa' : '#2563eb',
                            opacity: 0.8,
                          }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              '&:hover': {
                                color: isDark ? '#60a5fa' : '#2563eb',
                              }
                            }}
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                        backdropFilter: 'blur(10px)',
                        border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                        '&:hover': {
                          borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                        },
                        '&.Mui-focused': {
                          borderColor: isDark ? '#60a5fa' : '#2563eb',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        fontSize: '0.95rem',
                        padding: '12px 16px',
                      },
                    }}
                  />
                )}
              </Field>
            </Box>

            {/* Confirm Password Field */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{
                display: 'block',
                mb: 1,
                fontWeight: 600,
                color: isDark ? '#cbd5e1' : '#475569',
              }}>
                Confirm Password
              </Typography>
              <Field name="confirmPassword">
                {({ field, form, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{
                            color: isDark ? '#60a5fa' : '#2563eb',
                            opacity: 0.8,
                          }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              '&:hover': {
                                color: isDark ? '#60a5fa' : '#2563eb',
                              }
                            }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                        backdropFilter: 'blur(10px)',
                        border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                        '&:hover': {
                          borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                        },
                        '&.Mui-focused': {
                          borderColor: isDark ? '#60a5fa' : '#2563eb',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        fontSize: '0.95rem',
                        padding: '12px 16px',
                      },
                    }}
                  />
                )}
              </Field>
            </Box>

            {/* Password Requirements */}
            <Box sx={{
              p: 2,
              mt: 2,
              borderRadius: '8px',
              backgroundColor: isDark ? alpha('#1e293b', 0.3) : alpha('#f8fafc', 0.8),
              border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
            }}>
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: isDark ? '#cbd5e1' : '#475569',
                mb: 1,
                display: 'block',
              }}>
                Password must contain:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {[
                  'At least 8 characters',
                  'One uppercase letter',
                  'One lowercase letter',
                  'One number',
                  'One special character',
                ].map((requirement, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: isDark ? '#475569' : '#cbd5e1',
                    }} />
                    <Typography variant="caption" sx={{
                      color: isDark ? '#94a3b8' : '#64748b',
                    }}>
                      {requirement}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting}
            sx={{
              borderRadius: '12px',
              fontWeight: 600,
              py: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
              boxShadow: isDark
                ? '0 8px 25px rgba(16, 185, 129, 0.3)'
                : '0 8px 25px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isDark
                  ? '0 12px 35px rgba(16, 185, 129, 0.4)'
                  : '0 12px 35px rgba(16, 185, 129, 0.4)',
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              'Reset Password'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

// Forgot Password Modal with OTP and New Password Flow
const ForgotPasswordModal = ({ open, onClose, isDark }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  // const [otpSent, setOtpSent] = useState(false);
  // const [otpVerified, setOtpVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(false);

  const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

  // Start countdown for OTP resend
  const startCountdown = () => {
    setCountdown(60);
    setResendEnabled(false);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Update handleSendOtp function in LoginPage.js
  const handleSendOtp = async (values) => {
    setIsSubmitting(true);

    try {
      // Call the new API endpoint
      const response = await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: "sendPasswordResetOtp",
          email: values.email,
          apiKey: "DHRUVDARSHAN", // Your API key
          origin: window.location.origin
        })
      });

      const result = await response.json();
      console.log('Send OTP Response:', result);
      if (result.success) {
        // Always show success message (even if email doesn't exist - for security)
        alert(result.data.message === "If this email exists in our system, a password reset OTP has been sent" ? "Please enter the correct email id" : result.data.message);

        if (result.data.shopName) {
          // Email exists, proceed with OTP flow
          setEmail(values.email);
          // setOtpSent(true);
          startCountdown();
          setActiveStep(1);

          // Optional: Show personalized message
          console.log(`Password reset for: ${result.data.shopName}`);
        } else {
          // Email might not exist, but we still show success message
          // Keep user at same step
          console.log("Password reset requested - generic response");
        }
      } else {
        alert(response.error || "Failed to send OTP");
      }

    } catch (error) {
      console.error("Error sending OTP:", error);
      alert("Failed to send OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update handleVerifyOtp function
  const handleVerifyOtp = async (values) => {
    setIsSubmitting(true);

    try {
      // Call the new API endpoint
      const response = await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: "verifyPasswordResetOtp",
          email: email,
          otp: values.otp,
          apiKey: "DHRUVDARSHAN",
          origin: window.location.origin
        })
      });

      const result = await response.json();

      if (result.success) {
        // setOtpVerified(true);
        setActiveStep(2);
      } else {
        alert(result.error || "Invalid OTP");
      }

    } catch (error) {
      console.error("Error verifying OTP:", error);
      alert("Failed to verify OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update handleResetPassword function
  const handleResetPassword = async (values) => {
    setIsSubmitting(true);

    try {
      // Call the new API endpoint
      const response = await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: "resetPassword",
          email: email,
          otp: values.otp, // Include OTP for verification
          newPassword: values.newPassword,
          apiKey: "DHRUVDARSHAN",
          origin: window.location.origin
        })
      });

      const result = await response.json();

      if (result.success) {
        alert("Password reset successfully! You can now log in with your new password.");
        resetForm();
        onClose(); // Close the modal
      } else {
        alert(result.error || "Failed to reset password");
      }

    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update handleResendOtp function
  const handleResendOtp = async () => {
    if (!resendEnabled) return;

    setIsSubmitting(true);

    try {
      // Call the same endpoint as initial OTP request
      const response = await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: "sendPasswordResetOtp",
          email: email,
          apiKey: "DHRUVDARSHAN",
          origin: window.location.origin
        })
      });

      const result = await response.json();

      if (result.success) {
        alert("OTP resent successfully!");
        startCountdown();
      } else {
        alert(result.error || "Failed to resend OTP");
      }

    } catch (error) {
      console.error("Error resending OTP:", error);
      alert("Failed to resend OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const resetForm = () => {
    setActiveStep(0);
    setEmail('');
    // setOtpSent(false);
    // setOtpVerified(false);
    setCountdown(0);
    setResendEnabled(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.4)'
            : '0 20px 60px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{
        p: 3,
        background: isDark
          ? 'linear-gradient(90deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)'
          : 'linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)',
        borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
      }}>
        <Typography variant="h5" sx={{
          fontWeight: 700,
          color: isDark ? '#f1f5f9' : '#1e293b',
          textAlign: 'center',
        }}>
          Reset Your Password
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Stepper */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '0.75rem',
                      color: isDark ? '#94a3b8' : '#64748b',
                      '&.Mui-active': {
                        color: isDark ? '#60a5fa' : '#2563eb',
                        fontWeight: 600,
                      },
                      '&.Mui-completed': {
                        color: isDark ? '#10b981' : '#059669',
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step 1: Enter Email */}
        {activeStep === 0 && (
          <Formik
            initialValues={{ email: '' }}
            validationSchema={Yup.object({
              email: Yup.string()
                .required('Email is required')
                .email('Invalid email address'),
            })}
            onSubmit={handleSendOtp}
          >
            {({ values, errors, touched }) => (
              <Form>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{
                    color: isDark ? '#94a3b8' : '#64748b',
                    textAlign: 'center',
                    mb: 3,
                  }}>
                    Enter your email address to receive a verification code
                  </Typography>

                  <Field name="email">
                    {({ field, form, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="Enter your email"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{
                                color: isDark ? '#60a5fa' : '#2563eb',
                                opacity: 0.8,
                              }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                            backdropFilter: 'blur(10px)',
                            border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                            '&:hover': {
                              borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                            },
                            '&.Mui-focused': {
                              borderColor: isDark ? '#60a5fa' : '#2563eb',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#f1f5f9' : '#1e293b',
                            fontSize: '0.95rem',
                            padding: '12px 16px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: '12px',
                    fontWeight: 600,
                    py: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                      : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    boxShadow: isDark
                      ? '0 8px 25px rgba(96, 165, 250, 0.3)'
                      : '0 8px 25px rgba(37, 99, 235, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: isDark
                        ? '0 12px 35px rgba(96, 165, 250, 0.4)'
                        : '0 12px 35px rgba(37, 99, 235, 0.4)',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        )}

        {/* Step 2: Verify OTP */}
        {activeStep === 1 && (
          <Formik
            initialValues={{ otp: '' }}
            validationSchema={Yup.object({
              otp: Yup.string()
                .required('OTP is required')
                .matches(/^[0-9]{6}$/, 'OTP must be 6 digits'),
            })}
            onSubmit={handleVerifyOtp}
          >
            {({ values, errors, touched }) => (
              <Form>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{
                    color: isDark ? '#94a3b8' : '#64748b',
                    textAlign: 'center',
                    mb: 3,
                  }}>
                    Enter the 6-digit verification code sent to{' '}
                    <Typography component="span" sx={{
                      fontWeight: 600,
                      color: isDark ? '#60a5fa' : '#2563eb',
                    }}>
                      {email}
                    </Typography>
                  </Typography>

                  <Field name="otp">
                    {({ field, form, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        inputProps={{ maxLength: 6 }}
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                            backdropFilter: 'blur(10px)',
                            border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                            '&:hover': {
                              borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                            },
                            '&.Mui-focused': {
                              borderColor: isDark ? '#60a5fa' : '#2563eb',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#f1f5f9' : '#1e293b',
                            fontSize: '1.25rem',
                            textAlign: 'center',
                            letterSpacing: '8px',
                            padding: '12px 16px',
                          },
                        }}
                      />
                    )}
                  </Field>

                  {/* Resend OTP */}
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{
                      color: isDark ? '#94a3b8' : '#64748b',
                      mb: 1,
                    }}>
                      {countdown > 0 ? `Resend code in ${countdown}s` : 'Didn\'t receive the code?'}
                    </Typography>
                    <Button
                      onClick={handleResendOtp}
                      disabled={!resendEnabled || isSubmitting}
                      variant="text"
                      size="small"
                      sx={{
                        color: isDark ? '#60a5fa' : '#2563eb',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Resend OTP'}
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: '12px',
                    fontWeight: 600,
                    py: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                      : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    boxShadow: isDark
                      ? '0 8px 25px rgba(96, 165, 250, 0.3)'
                      : '0 8px 25px rgba(37, 99, 235, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: isDark
                        ? '0 12px 35px rgba(96, 165, 250, 0.4)'
                        : '0 12px 35px rgba(37, 99, 235, 0.4)',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Verify OTP'
                  )}
                </Button>

              </Form>
            )}
          </Formik>
        )}

        {/* Step 3: Reset Password */}
        {activeStep === 2 && (
          <ResetPasswordForm
            isSubmitting={isSubmitting}
            isDark={isDark}
            handleSubmit={handleResetPassword}
          />
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        pt: 0,
        justifyContent: 'space-between',
      }}>
        {activeStep > 0 && (
          <Button
            onClick={() => setActiveStep(activeStep - 1)}
            variant="outlined"
            startIcon={<ArrowBack />}
            sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.5),
              fontWeight: 500,
              '&:hover': {
                borderColor: alpha(isDark ? '#94a3b8' : '#64748b', 0.8),
              },
            }}
          >
            Back
          </Button>
        )}

        <Button
          onClick={handleClose}
          variant="text"
          sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 500,
            ml: 'auto',
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Login Page Component (UPDATED VERSION)
const LoginPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  // const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  React.useEffect(() => {
    const checkExistingSession = () => {
      if (sessionManager.isValidSession()) {
        navigate('/');
      }
    };

    checkExistingSession();
  }, [navigate]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .required('Email is required')
      .email('Invalid email address'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
  });

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    setLoginError('');

    try {
      const response = await login(values.email, values.password);

      if (response?.success) {
        const userData = {
          email: values.email,
          name: response.data?.name || 'User',
          shopName: response.data?.shopName || 'My Shop',
          phone: response.data?.phone || '',
          userId: response.data?.id || values.email,
          ...response.data
        };

        sessionManager.createSession(userData);
        localStorage.setItem("appAuth", "true");

        if (userData.name) {
          localStorage.setItem("userName", userData.name);
        }
        if (userData.shopName) {
          localStorage.setItem("shopName", userData.shopName);
        }

        navigate('/');
      } else {
        setLoginError(response?.message || 'Invalid email or password');
      }

    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Invalid email or password');
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        background: isDark
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 30%, #cbd5e1 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 20% 80%, rgba(96, 165, 250, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 80%, rgba(37, 99, 235, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)',
          zIndex: 1,
        },
      }}
    >
      {/* Background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(96, 165, 250, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 20s infinite ease-in-out',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
            '50%': { transform: 'translate(-50px, -50px) scale(1.1)' },
          },
        }}
      />

<Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgb(78, 135, 200) 100%)'
            : 'linear-gradient(156.2deg, rgb(1, 1, 1) 14.8%, rgba(32,104,177,1) 68.1%, rgb(169, 192, 218) 129%)',
          // filter: 'blur(40px)',
          // animation: 'float 15s infinite ease-in-out reverse',
        }}
      />

      {/* Modals */}
      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        isDark={isDark}
      />

      <TermsAndConditionsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        isDark={isDark}
      />

      <PrivacyPolicyModal
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        isDark={isDark}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Card sx={{
          borderRadius: '24px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
            : 'radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(148, 187, 233, 1) 100%)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 20px rgba(96, 165, 250, 0.1)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 20px rgba(37, 99, 235, 0.05)',
        }}>
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {/* Header Section */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}>
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)',
                border: `2px solid ${alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.3 : 0.2)}`,
                mb: 2,
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.2)',
              }}>
                <Store sx={{
                  fontSize: 40,
                  color: isDark ? '#60a5fa' : '#2563eb',
                }} />
              </Box>
              <Typography variant="h3" sx={{
                fontWeight: 900,
                // background: "linear-gradient(156.2deg, rgb(38, 41, 81) 14.8%, rgba(32,104,177,1) 68.1%, rgb(46, 85, 130) 80%)",
                background: isDark
                  ? 'radial-gradient(circle, rgb(196, 215, 227) 0%, rgb(87, 147, 215) 100%)'
                  : "linear-gradient(156.2deg, rgb(38, 41, 81) 14.8%, rgba(32,104,177,1) 68.1%, rgb(46, 85, 130) 80%)",
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                mb: 1,
                textAlign: 'center',
                fontSize: { xs: '2rem', sm: '2.5rem' },
              }}>
                JewelStock Pro
              </Typography>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: isDark ? '#94a3b8' : '#64748b',
                textAlign: 'center',
                mb: 1,
              }}>
                Welcome Back
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#64748b' : '#94a3b8',
                textAlign: 'center',
              }}>
                Sign in to your business account
              </Typography>
            </Box>

            {/* Error Message */}
            {loginError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                }}
                onClose={() => setLoginError('')}
              >
                {loginError}
              </Alert>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched }) => (
                <Form>
                  {/* Email Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{
                      display: 'block',
                      mb: 1,
                      fontWeight: 600,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.9rem',
                    }}>
                      Email Address
                    </Typography>
                    <Field name="email">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="email"
                          placeholder="Enter your email"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email sx={{
                                  color: isDark ? '#60a5fa' : '#2563eb',
                                  opacity: 0.8,
                                }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                              backdropFilter: 'blur(10px)',
                              border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                              },
                              '&.Mui-focused': {
                                borderColor: isDark ? '#60a5fa' : '#2563eb',
                                borderWidth: '2px',
                                backgroundColor: isDark ? alpha('#1e293b', 0.7) : alpha('#ffffff', 0.95),
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              fontSize: '0.95rem',
                              padding: '14px 16px',
                            },
                            '& .MuiFormHelperText-root': {
                              marginLeft: 0,
                              fontSize: '0.8rem',
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  {/* Password Field */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{
                      display: 'block',
                      mb: 1,
                      fontWeight: 600,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.9rem',
                    }}>
                      Password
                    </Typography>
                    <Field name="password">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{
                                  color: isDark ? '#60a5fa' : '#2563eb',
                                  opacity: 0.8,
                                }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  sx={{
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    '&:hover': {
                                      color: isDark ? '#60a5fa' : '#2563eb',
                                    }
                                  }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                              backdropFilter: 'blur(10px)',
                              border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                              '&:hover': {
                                borderColor: alpha(isDark ? '#60a5fa' : '#2563eb', isDark ? 0.5 : 0.4),
                              },
                              '&.Mui-focused': {
                                borderColor: isDark ? '#60a5fa' : '#2563eb',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              fontSize: '0.95rem',
                              padding: '14px 16px',
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  {/* Remember Me & Forgot Password */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent:"end",
                    alignItems: 'center',
                    mb: 4,
                  }}>
                    {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="remember-me" style={{ cursor: 'pointer' }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover .checkbox': {
                            borderColor: isDark ? '#60a5fa' : '#2563eb',
                          }
                        }}>
                          <Box className="checkbox" sx={{
                            background: '#fff',
                            width: 18,
                            height: 18,
                            borderRadius: '4px',
                            border: `2px solid ${alpha(isDark ? '#475569' : '#cbd5e1', isDark ? 0.4 : 0.6)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            ...(rememberMe && {
                              background: isDark ? '#60a5fa' : '#2563eb',
                              borderColor: isDark ? '#60a5fa' : '#2563eb',
                            }),
                          }}>
                            {rememberMe && (
                              <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '2px',
                                background: '#fff',
                              }} />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{
                            color: isDark ? '#cbd5e1' : '#475569',
                            fontSize: '0.875rem',
                            userSelect: 'none',
                          }}>
                            Remember me
                          </Typography>
                        </Box>
                      </label>
                    </Box> */}

                    <Button
  variant="text"
  onClick={() => setForgotPasswordOpen(true)}
  startIcon={<LockResetIcon />}
  sx={{
    color: isDark ? '#60a5fa' : '#2563eb',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none',
    '&:hover': {
      textDecoration: 'underline',
      background: 'none',
    },
    display: 'flex',
    alignItems: 'center',
  }}
>
  Forgot Password?
</Button>
                  </Box>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: '14px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2,
                      background: isDark
                        ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                        : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      boxShadow: isDark
                        ? '0 10px 30px rgba(96, 165, 250, 0.4)'
                        : '0 10px 30px rgba(37, 99, 235, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: isDark
                          ? '0 15px 40px rgba(96, 165, 250, 0.5)'
                          : '0 15px 40px rgba(37, 99, 235, 0.5)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        background: isDark
                          ? 'rgba(96, 165, 250, 0.3)'
                          : 'rgba(37, 99, 235, 0.3)',
                        color: isDark
                          ? 'rgba(241, 245, 249, 0.5)'
                          : 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                        Signing In...
                      </Box>
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  {/* Terms & Privacy */}
                  <Typography variant="caption" sx={{
                    color: isDark ? '#64748b' : '#595959',
                    textAlign: 'center',
                    display: 'block',
                    mt: 4,
                    lineHeight: 1.6,
                  }}>
                    By signing in, you agree to our{' '}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => setTermsOpen(true)}
                      sx={{
                        color: isDark ? '#60a5fa' : '#2563eb',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                          background: 'none',
                        }
                      }}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => setPrivacyOpen(true)}
                      sx={{
                        color: isDark ? '#60a5fa' : '#2563eb',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                          background: 'none',
                        }
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{
            color: isDark ? '#64748b' : '#94a3b8',
          }}>
            © {new Date().getFullYear()} JewelStock Pro. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;